import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Phone, Mail, MapPin, ExternalLink, Facebook, Linkedin } from "lucide-react";
import officialLogo from "@/assets/official-wle-logo.webp.asset.json";

const Footer = () => {
  return (
    <footer className="bg-foreground text-background">
      <div className="container py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <Link to="/" className="inline-flex" aria-label="WLE home">
              <img
                src={officialLogo.url}
                alt="WLE — People, Process, A Cleaner Tomorrow"
                className="h-auto w-56 max-w-full object-contain"
              />
            </Link>
            <p className="text-sm text-background/80">
              Malaysia's premier mechanical & system solutions provider. We deliver complete integrated solutions including custom tank fabrication, precision mechanical polishing, advanced electropolishing, and comprehensive EPCC turnkey projects from engineering to commissioning.
            </p>
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <div className="flex items-center space-x-2 bg-background/10 px-3 py-1 rounded-full">
                <div className="h-3 w-3 rounded-full bg-wle-red"></div>
                <span className="text-xs">Heated Tanks</span>
              </div>
              <div className="flex items-center space-x-2 bg-background/10 px-3 py-1 rounded-full">
                <div className="h-3 w-3 rounded-full bg-wle-blue"></div>
                <span className="text-xs">General Tanks</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Quick Links</h3>
            <div className="space-y-2">
              <Link to="/about" className="block text-sm text-background/80 hover:text-background transition-colors">
                About Us
              </Link>
              <Link to="/products" className="block text-sm text-background/80 hover:text-background transition-colors">
                Products
              </Link>
              <Link to="/tools" className="block text-sm text-background/80 hover:text-background transition-colors">
                Tools
              </Link>
              <Link to="/portfolio" className="block text-sm text-background/80 hover:text-background transition-colors">
                Portfolio
              </Link>
              <Link to="/industries" className="block text-sm text-background/80 hover:text-background transition-colors">
                Industries
              </Link>
              <Link to="/affiliations" className="block text-sm text-background/80 hover:text-background transition-colors">
                Affiliations
              </Link>
              <Link to="/privacy-policy" className="block text-sm text-background/80 hover:text-background transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms-of-service" className="block text-sm text-background/80 hover:text-background transition-colors">
                Terms of Service
              </Link>
              <Link to="/po-terms" className="block text-sm text-background/80 hover:text-background transition-colors">
                PO Terms & Conditions
              </Link>
            </div>
          </div>

          {/* Services */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Services</h3>
            <div className="space-y-2">
              <Link to="/services/tank-fabrication" className="block text-sm text-background/80 hover:text-background transition-colors">
                Tank Fabrication
              </Link>
              <Link to="/services" className="block text-sm text-background/80 hover:text-background transition-colors">
                Design & Build
              </Link>
              <Link to="/services" className="block text-sm text-background/80 hover:text-background transition-colors">
                Engineering Consultation
              </Link>
              <Link to="/services" className="block text-sm text-background/80 hover:text-background transition-colors">
                EPCC Turnkey Project
              </Link>
              <Link to="/services/mechanical-polishing" className="block text-sm text-background/80 hover:text-background transition-colors">
                Mechanical Polishing
              </Link>
              <Link to="/services/electropolishing" className="block text-sm text-background/80 hover:text-background transition-colors">
                Electro Polishing
              </Link>
            </div>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Contact Info</h3>
            <div className="space-y-3">
              <div className="flex items-start space-x-2">
                <MapPin className="h-4 w-4 mt-0.5 text-wle-blue" />
                <div className="text-sm text-background/80">
                  <p>7, Jalan Utarid U5/17,</p>
                  <p>Mahsing Integrated Industrial park,</p>
                  <p>Seksyen U5, 40150 Shah Alam,</p>
                  <p>Selangor Darul Ehsan. Malaysia</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Phone className="h-4 w-4 text-wle-blue" />
                <a 
                  href="tel:+601139069019" 
                  className="text-sm text-background/80 hover:text-background transition-colors"
                >
                  +6011 3906 9019
                </a>
              </div>
              <div className="flex items-center space-x-2">
                <Mail className="h-4 w-4 text-wle-blue" />
                <a 
                  href="mailto:enquiry@wletec.com" 
                  className="text-sm text-background/80 hover:text-background transition-colors"
                >
                  enquiry@wletec.com
                </a>
              </div>
              
              {/* Business Hours */}
              <div className="mt-4 space-y-2">
                <h4 className="font-semibold text-sm">Business Hours (UTC+8)</h4>
                <div className="text-sm text-background/80 space-y-1">
                  <p className="font-medium text-background/90">Office:</p>
                  <p>Mon - Fri: 8:00 AM - 5:00 PM</p>
                  <p>Saturday: 8:00 AM - 1:00 PM</p>
                  <p className="font-medium text-background/90 mt-2">Factory:</p>
                  <p>Mon - Sat: 8:00 AM - 5:00 PM</p>
                </div>
              </div>
              
              <Button asChild variant="outline" size="sm" className="mt-4">
                <a href="https://wa.me/601139069019" target="_blank" rel="noopener noreferrer">
                  WhatsApp Us
                  <ExternalLink className="h-3 w-3 ml-1" />
                </a>
              </Button>
              
              {/* Social Media Links */}
              <div className="mt-6 space-y-3">
                <h4 className="font-semibold text-sm">Follow Us</h4>
                <div className="flex gap-3">
                  <a 
                    href="https://www.facebook.com/WLEtech" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="p-2 rounded-full bg-wle-blue hover:bg-wle-blue/80 text-white transition-colors"
                    aria-label="WLE Tank Fabrication Facebook"
                  >
                    <Facebook className="h-4 w-4" />
                  </a>
                  <a 
                    href="https://www.facebook.com/profile.php?id=100054675420087" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="p-2 rounded-full bg-wle-red hover:bg-wle-red/80 text-white transition-colors"
                    aria-label="WLE Electro Polish Facebook"
                  >
                    <Facebook className="h-4 w-4" />
                  </a>
                  <a 
                    href="https://www.linkedin.com/in/edwin-wong-wle/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="p-2 rounded-full bg-wle-blue hover:bg-wle-blue/80 text-white transition-colors"
                    aria-label="Connect with our Director Edwin Wong on LinkedIn"
                  >
                    <Linkedin className="h-4 w-4" />
                  </a>
                </div>
                <p className="text-xs text-background/60">Connect with our Director on LinkedIn</p>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-background/20 mt-8 pt-8 text-center">
          <p className="text-sm text-background/60">
            © {new Date().getFullYear()} WLE. All rights reserved. | Malaysia Tank Fabricator, Custom to Perfection
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;